import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { useSeo } from "../../lib/seo";
import { useT } from "../../lib/i18n";
import { usePacks, PackPhoto, fmtEur } from "../../components/site/PackSection";
import { NotFound } from "./Pages";

export default function PackPage() {
  const { id } = useParams();
  const { t } = useT();
  const packs = usePacks();
  const p = packs?.find((x) => x.id === id);
  useSeo({ title: p ? `${p.name} — ${p.headline}` : "Pack", path: `/pack/${id}`, description: p?.tagline });
  if (packs === null) return <p className="k-section text-sm">Chargement…</p>;
  if (!p) return <NotFound />;
  const unit = p.price_ht / p.quantity;
  return (
    <article className="k-section" data-testid="pack-page">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <p className="k-eyebrow">{t("Offre de saison")} — {p.name}</p>
          <h1 className="k-h1 mt-5">{p.headline}</h1>
          <p className="k-lead mt-6 max-w-xl">{p.tagline}</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {p.variants.map((v) => (
              <div key={v.key} className="k-card overflow-hidden" data-testid={`pack-variant-${v.key}`}>
                <PackPhoto src={v.photo_page || v.photo} art={v.art} alt={`${v.label} — ${p.name}`} illustration={v.photo_is_illustration} className="aspect-[4/3]" position="center 25%" />
                <div className="p-5">
                  <p className="font-display text-2xl">{p.quantity} {v.plural}</p>
                  <p className="text-sm text-[#57534E] mt-1">{t("Coloris")} : {v.colors.join(", ")}{v.reference && <> · {t("Réf.")} {v.reference}</>}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-5xl" data-testid="pack-page-price">{fmtEur(p.price_ht)} <span className="text-base font-sans text-[#57534E]">HT {t("le pack")}</span></span>
            <span className="text-sm text-[#57534E]">— {fmtEur(unit)} HT {t("par pièce")} · {t("deux variantes séparées, sans mélange")}</span>
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={`/demande/vetements?pack=${p.id}`} className="k-btn" data-testid="pack-page-cta">{p.cta} <ArrowRight size={16} /></Link>
            <Link to="/demande/vetements" className="k-btn-outline" data-testid="pack-adapt-link">{t("Adapter ce pack à mon effectif")}</Link>
          </div>
          <p className="text-xs text-[#8C857B] mt-3">{t("Le prix du pack vaut pour")} {p.quantity} {t("pièces exactement : pour un autre effectif, nous établissons un devis dédié.")}</p>
        </div>
        <aside className="lg:col-span-5 grid gap-6 content-start">
          <PackPhoto src={p.detail_photo} alt="Broderie réalisée à l'atelier" illustration={p.detail_photo_is_illustration} credit={p.detail_photo_credit} className="aspect-[4/3]" position="center 45%" />
          {p.detail_photo_2 && <PackPhoto src={p.detail_photo_2} alt="Matière : molleton gratté, poche kangourou et bord-côte" credit="Matière" className="aspect-[4/3] -mt-2" position="center 45%" />}
          {p.photo_credit && <p className="text-[11px] text-[#8C857B] -mt-4">{p.photo_credit}</p>}
          <p className="text-xs text-[#8C857B] -mt-4" data-testid="pack-proto-page">{t("Devis validé : l'atelier réalise un prototype et vous envoie sa photo avant de lancer la série.")}</p>
          <section className="k-card p-6" data-testid="pack-inclusions">
            <h2 className="font-display text-2xl">{t("Compris dans le pack")}</h2>
            <ul className="mt-4 grid gap-2 text-sm">{p.inclusions.map((x) => <li key={x} className="flex gap-2"><Check size={16} className="text-[#2F7D5B] shrink-0 mt-0.5" />{x}</li>)}</ul>
            <h2 className="font-display text-2xl mt-8">{t("Non compris")}</h2>
            <ul className="mt-4 grid gap-2 text-sm text-[#57534E]">{p.exclusions.map((x) => <li key={x} className="flex gap-2"><Minus size={16} className="shrink-0 mt-0.5" />{x}</li>)}</ul>
          </section>
          <section className="border border-[#1C1917] p-6" data-testid="pack-logo-conditions">
            <p className="k-eyebrow">{t("Conditions du logo")}</p>
            <ul className="mt-3 grid gap-1.5 text-sm">
              <li>{t("Même logo sur les")} {p.quantity} {t("pièces, broderie classique uniquement.")}</li>
              <li>{t("Taille maximale")} : {p.logo_limits.max_cm} × {p.logo_limits.max_cm} cm.</li>
              <li>{t("Jusqu'à")} {p.logo_limits.max_colors} {t("couleurs de fil")}.</li>
              <li>{t("Jusqu'à")} {p.logo_limits.max_stitches.toLocaleString("fr-FR")} {t("points de broderie")}.</li>
            </ul>
            <p className="text-xs text-[#57534E] mt-4">{t("Tout dépassement nécessite une adaptation du logo ou un supplément, accepté avant commande. Votre demande donne lieu à un devis, sans commande automatique.")}</p>
          </section>
        </aside>
      </div>
    </article>
  );
}
